import { apiClient } from "./apiClient";
import type {
  GenerateBatchResult,
  GenerateStoreResult,
} from "./distributionService";

export type PreviewStatus =
  | "pending"
  | "approved"
  | "cancelled"
  | "expired";

export interface TransferPreviewSession {
  id: string;
  request_id: string;
  store_mxp_code: string | null;
  status: PreviewStatus;
  excluded_recommendation_ids: string[];
  created_by: string | null;
  created_at: string;
  expires_at: string;
  approved_at: string | null;
  cancelled_at: string | null;
  picking_ids_created: number[] | null;
}

export interface EnrichedLine {
  recommendation_id: string;
  unified_product_id: string;
  product_name: string | null;
  abc: string | null;
  category_key: string | null;
  qty_planned: number;
  pack_size: number;
  source_type: "seeding" | "stock_alert" | "reorder" | "reposicion";
  origin_warehouse: string;
  reclaims_denial: boolean;
  denial_id: number | null;
  denial: { qty_denied: number; age_days: number } | null;
  deficit_value: number;
  stock_en_origen: number;
  stock_suficiente: boolean;
  supplier_id: number | null;
  supplier_name: string | null;
}

export interface EnrichedDeferredLine {
  recommendation_id: string;
  unified_product_id: string;
  product_name: string | null;
  qty_planned: number;
  source_type: "seeding" | "stock_alert" | "reorder" | "reposicion";
  origin_warehouse: string;
  category_key: string | null;
  deferred_reason: "category_cap" | "total_cap";
  cap_category_key: string | null;
  cap_limit: number;
  cap_accumulated: number;
  supplier_id: number | null;
  supplier_name: string | null;
  abc: string | null;
  stock_en_origen: number;
}

/**
 * Auditoría 2026-05-05 — el dialog de Revisión previa agrupa diferidas
 * por proveedor (no categoría). Cada bucket lleva agregados (`total_lines`,
 * `total_units`) calculados en backend.
 */
export interface DeferredLinesBySupplier {
  supplier_id: number | null;
  supplier_name: string | null;
  total_lines: number;
  total_units: number;
  lines: EnrichedDeferredLine[];
}

export interface EnrichedStoreBlock {
  store_mxp_code: string;
  lines: EnrichedLine[];
  deferred: DeferredLinesBySupplier[];
  capacity: {
    max_units_total: number | null;
    max_units_by_category: Record<string, number>;
  } | null;
}

export interface RepromoteResult {
  added: string[];
  rejected: { id: string; reason: string }[];
}

export interface PreviewResponse {
  session: TransferPreviewSession;
  is_expired: boolean;
  enriched_stores: EnrichedStoreBlock[];
}

export interface ApproveResponse {
  session: TransferPreviewSession;
  result: GenerateStoreResult | GenerateBatchResult;
}

export const distributionPreviewService = {
  async createPreview(input: {
    store?: string;
    request_id?: string;
    force_override?: boolean;
  }): Promise<PreviewResponse> {
    const res = await apiClient.post<{ ok: true; data: PreviewResponse }>(
      `/distribution/preview`,
      input,
    );
    return res.data.data;
  },

  async getPreview(id: string): Promise<PreviewResponse> {
    const res = await apiClient.get<{ ok: true; data: PreviewResponse }>(
      `/distribution/preview/${encodeURIComponent(id)}`,
    );
    return res.data.data;
  },

  async approvePreview(
    id: string,
    body: { excluded_recommendation_ids?: string[] },
  ): Promise<ApproveResponse> {
    const res = await apiClient.post<{ ok: true; data: ApproveResponse }>(
      `/distribution/preview/${encodeURIComponent(id)}/approve`,
      body,
    );
    return res.data.data;
  },

  async cancelPreview(id: string): Promise<TransferPreviewSession> {
    const res = await apiClient.post<{ ok: true; data: TransferPreviewSession }>(
      `/distribution/preview/${encodeURIComponent(id)}/cancel`,
      {},
    );
    return res.data.data;
  },

  /**
   * Auditoría 2026-05-05 — re-promueve líneas diferidas (por capacity cap)
   * desde el dialog. Backend valida stock en origen y marca
   * repromoted_in_preview=true por reco. Idempotente.
   */
  async repromoteDeferred(
    id: string,
    body: { deferred_recommendation_ids: string[] },
  ): Promise<RepromoteResult> {
    const res = await apiClient.post<{ ok: true; data: RepromoteResult }>(
      `/distribution/preview/${encodeURIComponent(id)}/repromote`,
      body,
    );
    return res.data.data;
  },
};
